import React, { useEffect, useState, useRef } from "react";
import Button from "../../Common/Buttons/Buttons";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useNpiVerificationMutation } from "@/Redux/apis/auth";
import { userData } from "@/Redux/apis/userData";
import { toast } from "react-toastify";
import { createPortal } from "react-dom";
import CrossIcon from "@/Components/Common/Icons/CrossIcon";
const NpiVerification = ({ closeModal }) => {
  const users = useSelector((state) => state?.userData?.userData?.user);
  const token = useSelector((state) => state?.token?.token);
  const [npiError, setNpiError ] = useState();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [npiNumber, setNpiNumber] = useState(null);
  const [loading, setLoading] = useState(false);

  const [npiNumberVerification, response] = useNpiVerificationMutation();
  const router = useRouter();
  const dispatch = useDispatch();
  const onSubmit = async (data) => {
    setLoading(true);
    const body = {
      id: users?._id,
      npi_number: Number(data?.npi_number),
    };
    await npiNumberVerification(body);
  };
  useEffect(() => {
    const handleResponse = async () => {
      if (response.isLoading) {
        setLoading(true);
      } else if (response.isSuccess) {
        setLoading(false);
        const data = { token: token };
        await dispatch(userData(data));
        toast.success("NPI number verified successfully");
        closeModal();
      } else if (response.isError) {
        if (response?.error?.data?.message === "NPI number is not valid" || response?.error?.data?.message === "NPI belongs to another user") {
          setNpiError({
            error: response?.error?.data?.message,
          });
        } else {
          toast.error(response?.error?.data?.message || "Error verifying NPI number",)
          closeModal();
        }
        setLoading(false);
      }
    };

    handleResponse();
  }, [response, dispatch, closeModal]);

  const onChangeHandler = (e) => {
    setNpiNumber(Number(e.target.value));
  };

  const [mounted, setMounted] = useState(false);
  const portalContainerRef = useRef(null);

  useEffect(() => {
    // Create portal container
    let container = document.getElementById('npi-verification-portal');
    
    if (!container) {
      container = document.createElement('div');
      container.id = 'npi-verification-portal';
      document.body.appendChild(container);
    }
    
    portalContainerRef.current = container;
    setMounted(true);
    
    // Prevent body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
      try {
        const containerToRemove = portalContainerRef.current;
        if (containerToRemove && document.body.contains(containerToRemove)) {
          document.body.removeChild(containerToRemove);
        }
        portalContainerRef.current = null;
      } catch (error) {
        console.warn('Portal container cleanup:', error);
      }
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (!mounted || !portalContainerRef.current) return null;

  const modalContent = (
    <div
      className="fixed inset-0 popup-bg flex justify-center z-[9999999] items-end sm:items-center"
      onClick={handleBackdropClick}
    >
      <div className="w-full sm:w-[600px] lg:w-[950px] bg-white z-50 rounded-none sm:rounded-xl shadow-2xl border border-neutral-200 overflow-hidden h-full sm:h-auto flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-neutral-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-base sm:text-xl font-bold text-gray-900 font-poppins">
              NPI Verification
            </h2>
            <div
              onClick={closeModal}
              className="cursor-pointer hover:bg-gray-100 rounded-full p-1 transition-colors"
            >
              <CrossIcon />
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="w-full flex-1 flex flex-col overflow-hidden">
          {/* Scrollable Content */}
          <div className="px-4 sm:px-6 py-4 sm:py-6 flex-1 overflow-y-auto sm:overflow-visible">
            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-semibold text-gray-900 font-poppins mb-1.5 sm:mb-2">
                NPI Number <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <div
                className={`${
                  errors.npi_number || npiError?.error
                    ? "border-2 border-red-500"
                    : "border-2 border-gray-200 focus-within:border-primary-primaryBlue"
                } px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-colors`}
              >
                <input
                  {...register("npi_number", {
                    required: "NPI number is required",
                    pattern: {
                      value: /^\d{10}$/,
                      message: "NPI number should be 10 digits"
                    }
                  })}
                  name="npi_number"
                  value={npiNumber || ""}
                  onChange={onChangeHandler}
                  type="number"
                  placeholder="Enter 10-digit NPI number"
                  className="w-full border-none outline-none text-sm font-poppins bg-transparent"
                />
              </div>
              {errors.npi_number && (
                <div className="mt-1.5 sm:mt-2 text-red-600 text-xs sm:text-sm font-poppins">
                  {errors.npi_number.message}
                </div>
              )}
              {npiError?.error && (
                <div className="mt-1.5 sm:mt-2 text-red-600 text-xs sm:text-sm font-poppins">
                  {npiError?.error}
                </div>
              )}
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-lg mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-gray-700 font-poppins leading-relaxed">
                Your NPI (National Provider Identifier) number helps verify your professional credentials and ensures the authenticity of your reviews.
              </p>
            </div>
          </div>

          {/* Fixed Bottom Buttons */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex gap-2 sm:gap-3 flex-shrink-0 mt-auto bg-white">
            <button
              type="button"
              onClick={closeModal}
              className="flex-1 border-2 border-gray-300 text-gray-700 text-sm sm:text-base font-semibold font-poppins rounded-lg py-2.5 sm:py-3 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 text-white rounded-lg text-sm sm:text-base font-semibold font-poppins py-2.5 sm:py-3 transition-all ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg sm:transform sm:hover:scale-[1.02]"
              }`}
            >
              {loading ? "Verifying..." : "Verify NPI"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      {portalContainerRef.current && createPortal(modalContent, portalContainerRef.current)}
    </>
  );
};
export default NpiVerification;
